<script lang="ts">
  /**
   * Friendly first-run dialog that asks the student for a profile photo. The
   * student picks (or takes, on mobile) a photo; we downscale it client-side to
   * a small square JPEG and POST it. "Later" skips without saving — the caller
   * re-prompts next session until an image is set.
   *
   * Emits:
   *   saved  { detail: string }  — the stored data URL (also reflected upward)
   *   skip                        — student chose "Later"
   */
  import { createEventDispatcher } from "svelte";
  import { _ } from "./i18n";
  import { setMyProfileImage, ApiError } from "./api";
  import { renderProfileRenditions, type ProfileRenditions } from "./image";
  import Avatar from "./Avatar.svelte";
  import WebcamCapture from "./WebcamCapture.svelte";

  /** Display name / email for the fallback preview before a photo is chosen. */
  export let name: string | null = null;

  const dispatch = createEventDispatcher<{ saved: string; skip: void }>();

  let fileInput: HTMLInputElement | undefined;
  let preview: string | null = null;
  /** High-res 16:9 full rendition staged alongside the square `preview`. */
  let fullData: string | null = null;
  let saving = false;
  let error: string | null = null;
  /** When true, the in-app webcam capture overlay is shown above the prompt. */
  let showCamera = false;

  function pick() {
    fileInput?.click();
  }

  /** Frame captured & rendered by the webcam component → stage both renditions. */
  function onCapture(e: CustomEvent<ProfileRenditions>) {
    error = null;
    preview = e.detail.thumb;
    fullData = e.detail.full;
    showCamera = false;
  }

  async function onFile(e: Event) {
    error = null;
    const input = e.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    try {
      const r = await renderProfileRenditions(file);
      preview = r.thumb;
      fullData = r.full;
    } catch {
      error = $_("profile.error");
      preview = null;
      fullData = null;
    } finally {
      // Allow re-picking the same file.
      input.value = "";
    }
  }

  async function save() {
    if (!preview || saving) return;
    saving = true;
    error = null;
    try {
      const res = await setMyProfileImage(preview, fullData);
      dispatch("saved", res.image ?? preview);
    } catch (err) {
      if (err instanceof ApiError && err.status === 413) {
        error = $_("profile.tooLarge");
      } else {
        error = $_("profile.error");
      }
    } finally {
      saving = false;
    }
  }

  function skip() {
    dispatch("skip");
  }
</script>

<div class="overlay" data-testid="profile-prompt" role="dialog" aria-modal="true">
  <div class="card">
    <h2 class="title">{$_("profile.title")}</h2>
    <p class="help">{$_("profile.help")}</p>

    <div class="preview">
      <Avatar image={preview} {name} size={140} label={$_("profile.placeholderAlt")} />
    </div>

    {#if error}
      <p class="error" data-testid="profile-error">{error}</p>
    {/if}

    <!-- accept=image/* + capture lets mobile offer the camera directly -->
    <input
      bind:this={fileInput}
      type="file"
      accept="image/*"
      capture="user"
      class="file"
      data-testid="profile-file"
      on:change={onFile}
    />

    <div class="actions">
      <button
        type="button"
        class="btn ghost"
        data-testid="profile-skip"
        on:click={skip}
      >
        {$_("profile.skip")}
      </button>
      <button
        type="button"
        class="btn"
        data-testid="profile-choose"
        on:click={pick}
      >
        {preview ? $_("profile.retake") : $_("profile.choose")}
      </button>
      <button
        type="button"
        class="btn"
        data-testid="profile-camera-open"
        on:click={() => (showCamera = true)}
      >
        {$_("profile.takePhoto")}
      </button>
      <button
        type="button"
        class="btn primary"
        data-testid="profile-save"
        disabled={!preview || saving}
        on:click={save}
      >
        {saving ? $_("profile.saving") : $_("profile.save")}
      </button>
    </div>
  </div>
</div>

{#if showCamera}
  <WebcamCapture on:capture={onCapture} on:close={() => (showCamera = false)} />
{/if}

<style>
  .overlay {
    position: fixed;
    inset: 0;
    display: grid;
    place-items: center;
    background: rgba(15, 23, 42, 0.7);
    z-index: 50;
    padding: 1rem;
    /* Full-screen overlay under the iOS status bar / home indicator; keep the
       card's title and action buttons tappable. env() is 0 elsewhere. */
    padding-top: max(1rem, env(safe-area-inset-top));
    padding-bottom: max(1rem, env(safe-area-inset-bottom));
  }
  .card {
    width: min(26rem, 100%);
    background: #1f2937;
    color: #f3f4f6;
    border-radius: 1rem;
    padding: 1.5rem;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
    text-align: center;
  }
  .title {
    margin: 0 0 0.25rem;
    font-size: 1.25rem;
  }
  .help {
    margin: 0 0 1rem;
    color: #cbd5e1;
    font-size: 0.95rem;
  }
  .preview {
    display: grid;
    place-items: center;
    margin-bottom: 1rem;
  }
  .error {
    color: #fca5a5;
    margin: 0 0 0.75rem;
    font-size: 0.9rem;
  }
  .file {
    display: none;
  }
  .actions {
    display: flex;
    gap: 0.5rem;
    justify-content: center;
    flex-wrap: wrap;
  }
  .btn {
    appearance: none;
    border: 1px solid rgba(255, 255, 255, 0.18);
    background: #374151;
    color: #f3f4f6;
    border-radius: 0.6rem;
    padding: 0.6rem 1rem;
    font-size: 0.95rem;
    cursor: pointer;
  }
  .btn.ghost {
    background: transparent;
  }
  .btn.primary {
    background: #2563eb;
    border-color: #2563eb;
  }
  .btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
</style>
